import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

/**
 * מסך בקונסולת הניהול: כותרת, תת כותרת, פעולות בצד הסוף, ותוכן
 * ברוחב דסקטופ. פעולות יושבות ליד הכותרת ולא בתחתית המסך, כי בדסקטופ
 * אין "כפתור ראשי דביק" ואין טעם לגלול כדי להגיע אליו.
 */
export function AdminPage({
  title,
  subtitle,
  actions,
  backTo,
  children,
  width = 'wide',
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** קישור חזרה. מוצג רק במסך פנימי; מסך שורש בניווט לא צריך אותו. */
  backTo?: string;
  children: ReactNode;
  /** narrow לטפסים והגדרות, wide לטבלאות ולוחות. */
  width?: 'narrow' | 'wide' | 'full';
}) {
  return (
    <div className="grid min-h-dvh content-start">
      <header className="border-b border-line bg-surface px-4 py-4 lg:px-8 lg:py-6">
        <div className={cn('mx-auto w-full min-w-0', CONTENT_WIDTH[width])}>
          {backTo && (
            <Link
              to={backTo}
              className="mb-1 inline-flex min-h-9 items-center gap-1 text-body-sm text-muted no-underline hover:text-ink"
            >
              <ChevronRight size={18} strokeWidth={1.75} aria-hidden="true" />
              חזרה
            </Link>
          )}
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div className="grid min-w-0 gap-1">
              <h1 className="m-0 font-display text-display-m font-semibold lg:text-display-l">
                {title}
              </h1>
              {subtitle && <p className="m-0 text-body-sm text-muted">{subtitle}</p>}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
          </div>
        </div>
      </header>

      <div className="px-4 py-4 pb-10 lg:px-8 lg:py-6">
        <div className={cn('mx-auto grid w-full min-w-0 gap-4 lg:gap-6', CONTENT_WIDTH[width])}>
          {children}
        </div>
      </div>
    </div>
  );
}

const CONTENT_WIDTH = {
  narrow: 'max-w-screen-md',
  wide: 'max-w-screen-xl',
  full: 'max-w-none',
} as const;

/**
 * קופסת תוכן של הקונסולה. בדסקטופ היא הגבול בין אזורים, במקום
 * כרטיסים נפרדים שמפוזרים על המסך.
 */
export function Panel({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('overflow-hidden rounded-md border border-line bg-surface', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          {title && <h2 className="m-0 text-title font-semibold">{title}</h2>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={cn(bodyClassName ?? 'p-4')}>{children}</div>
    </section>
  );
}
